/**
 * Keyboard shortcut customization and preset management
 */

export interface CustomShortcut {
  id: string; // Unique identifier for the action (e.g., 'copy-color', 'paste-color')
  key: string;
  ctrlOrCmd?: boolean;
  shift?: boolean;
  alt?: boolean;
}

export interface ShortcutPreset {
  id: string;
  name: string;
  description: string;
  shortcuts: Record<string, CustomShortcut>;
}

/**
 * Default keyboard shortcuts (Chromis standard)
 */
export const DEFAULT_SHORTCUTS: Record<string, CustomShortcut> = {
  'copy-color': {
    id: 'copy-color',
    key: 'c',
    ctrlOrCmd: true,
  },
  'paste-color': {
    id: 'paste-color',
    key: 'v',
    ctrlOrCmd: true,
  },
  'focus-input': {
    id: 'focus-input',
    key: 'k',
    ctrlOrCmd: true,
  },
  'save-palette': {
    id: 'save-palette',
    key: 's',
    ctrlOrCmd: true,
  },
  'show-help': {
    id: 'show-help',
    key: '/',
    ctrlOrCmd: true,
  },
  'close-modal': {
    id: 'close-modal',
    key: 'Escape',
  },
  'undo': {
    id: 'undo',
    key: 'z',
    ctrlOrCmd: true,
  },
  'redo': {
    id: 'redo',
    key: 'y',
    ctrlOrCmd: true,
  },
};

/**
 * Photoshop-inspired preset
 */
export const PHOTOSHOP_PRESET: ShortcutPreset = {
  id: 'photoshop',
  name: 'Photoshop',
  description: 'Keyboard shortcuts inspired by Adobe Photoshop',
  shortcuts: {
    'copy-color': {
      id: 'copy-color',
      key: 'c',
      ctrlOrCmd: true,
    },
    'paste-color': {
      id: 'paste-color',
      key: 'v',
      ctrlOrCmd: true,
    },
    'focus-input': {
      id: 'focus-input',
      key: 'i', // Photoshop uses I for Eyedropper
    },
    'save-palette': {
      id: 'save-palette',
      key: 's',
      ctrlOrCmd: true,
    },
    'show-help': {
      id: 'show-help',
      key: 'F1',
    },
    'close-modal': {
      id: 'close-modal',
      key: 'Escape',
    },
    'undo': {
      id: 'undo',
      key: 'z',
      ctrlOrCmd: true,
    },
    'redo': {
      id: 'redo',
      key: 'z',
      ctrlOrCmd: true,
      shift: true,
    },
  },
};

/**
 * VS Code-inspired preset
 */
export const VSCODE_PRESET: ShortcutPreset = {
  id: 'vscode',
  name: 'VS Code',
  description: 'Keyboard shortcuts inspired by Visual Studio Code',
  shortcuts: {
    'copy-color': {
      id: 'copy-color',
      key: 'c',
      ctrlOrCmd: true,
    },
    'paste-color': {
      id: 'paste-color',
      key: 'v',
      ctrlOrCmd: true,
    },
    'focus-input': {
      id: 'focus-input',
      key: 'p',
      ctrlOrCmd: true,
    },
    'save-palette': {
      id: 'save-palette',
      key: 's',
      ctrlOrCmd: true,
    },
    'show-help': {
      id: 'show-help',
      key: '?',
      ctrlOrCmd: true,
      shift: true,
    },
    'close-modal': {
      id: 'close-modal',
      key: 'Escape',
    },
    'undo': {
      id: 'undo',
      key: 'z',
      ctrlOrCmd: true,
    },
    'redo': {
      id: 'redo',
      key: 'z',
      ctrlOrCmd: true,
      shift: true,
    },
  },
};

/**
 * All available presets
 */
export const SHORTCUT_PRESETS: ShortcutPreset[] = [
  {
    id: 'default',
    name: 'Default',
    description: 'Chromis standard keyboard shortcuts',
    shortcuts: DEFAULT_SHORTCUTS,
  },
  PHOTOSHOP_PRESET,
  VSCODE_PRESET,
];

/**
 * Action metadata for UI display
 */
export interface ShortcutAction {
  id: string;
  label: string;
  description: string;
  category: string;
  allowCustomization: boolean;
}

export const SHORTCUT_ACTIONS: ShortcutAction[] = [
  {
    id: 'copy-color',
    label: 'Copy Color',
    description: 'Copy the current color to clipboard',
    category: 'Color Operations',
    allowCustomization: true,
  },
  {
    id: 'paste-color',
    label: 'Paste Color',
    description: 'Paste color from clipboard',
    category: 'Color Operations',
    allowCustomization: true,
  },
  {
    id: 'focus-input',
    label: 'Focus Color Input',
    description: 'Focus the color input field',
    category: 'Navigation',
    allowCustomization: true,
  },
  {
    id: 'save-palette',
    label: 'Save Palette',
    description: 'Save the current palette',
    category: 'Palette Operations',
    allowCustomization: true,
  },
  {
    id: 'show-help',
    label: 'Show Keyboard Shortcuts',
    description: 'Display keyboard shortcuts help',
    category: 'Help',
    allowCustomization: true,
  },
  {
    id: 'close-modal',
    label: 'Close Modal/Panel',
    description: 'Close the currently open modal or panel',
    category: 'Navigation',
    allowCustomization: false, // Escape should always close
  },
  {
    id: 'undo',
    label: 'Undo',
    description: 'Undo the last action',
    category: 'Editing',
    allowCustomization: true,
  },
  {
    id: 'redo',
    label: 'Redo',
    description: 'Redo the last undone action',
    category: 'Editing',
    allowCustomization: true,
  },
];

/**
 * LocalStorage key for custom shortcuts
 */
const STORAGE_KEY = 'chromis-custom-shortcuts';
const PRESET_KEY = 'chromis-shortcut-preset';

/**
 * Load custom shortcuts from localStorage
 */
export function loadCustomShortcuts(): Record<string, CustomShortcut> {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) return DEFAULT_SHORTCUTS;
    
    const parsed = JSON.parse(stored);
    // Merge with defaults to ensure all actions have shortcuts
    return { ...DEFAULT_SHORTCUTS, ...parsed };
  } catch (error) {
    console.error('Failed to load custom shortcuts:', error);
    return DEFAULT_SHORTCUTS;
  }
}

/**
 * Save custom shortcuts to localStorage
 */
export function saveCustomShortcuts(shortcuts: Record<string, CustomShortcut>): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(shortcuts));
  } catch (error) {
    console.error('Failed to save custom shortcuts:', error);
  }
}

/**
 * Load current preset ID
 */
export function loadCurrentPreset(): string {
  try {
    return localStorage.getItem(PRESET_KEY) || 'default';
  } catch {
    return 'default';
  }
}

/**
 * Save current preset ID
 */
export function saveCurrentPreset(presetId: string): void {
  try {
    localStorage.setItem(PRESET_KEY, presetId);
  } catch (error) {
    console.error('Failed to save preset:', error);
  }
}

/**
 * Apply a preset
 */
export function applyPreset(presetId: string): Record<string, CustomShortcut> {
  const preset = SHORTCUT_PRESETS.find((p) => p.id === presetId);
  if (!preset) return DEFAULT_SHORTCUTS;
  
  saveCustomShortcuts(preset.shortcuts);
  saveCurrentPreset(presetId);
  return preset.shortcuts;
}

/**
 * Reset to default shortcuts
 */
export function resetToDefaults(): Record<string, CustomShortcut> {
  saveCustomShortcuts(DEFAULT_SHORTCUTS);
  saveCurrentPreset('default');
  return DEFAULT_SHORTCUTS;
}

/**
 * Detect conflicts between shortcuts
 */
export function detectConflicts(
  shortcuts: Record<string, CustomShortcut>
): Array<{ action1: string; action2: string; shortcut: CustomShortcut }> {
  const conflicts: Array<{ action1: string; action2: string; shortcut: CustomShortcut }> = [];
  const entries = Object.entries(shortcuts);

  for (let i = 0; i < entries.length; i++) {
    for (let j = i + 1; j < entries.length; j++) {
      const [id1, shortcut1] = entries[i];
      const [id2, shortcut2] = entries[j];

      if (shortcutsMatch(shortcut1, shortcut2)) {
        conflicts.push({
          action1: id1,
          action2: id2,
          shortcut: shortcut1,
        });
      }
    }
  }

  return conflicts;
}

/**
 * Check if two shortcuts match
 */
function shortcutsMatch(s1: CustomShortcut, s2: CustomShortcut): boolean {
  return (
    s1.key.toLowerCase() === s2.key.toLowerCase() &&
    !!s1.ctrlOrCmd === !!s2.ctrlOrCmd &&
    !!s1.shift === !!s2.shift &&
    !!s1.alt === !!s2.alt
  );
}

/**
 * Validate a shortcut assignment
 */
export function validateShortcut(
  actionId: string,
  shortcut: CustomShortcut,
  currentShortcuts: Record<string, CustomShortcut>
): { valid: boolean; error?: string } {
  // Check if key is valid
  if (!shortcut.key || shortcut.key.trim() === '') {
    return { valid: false, error: 'Key cannot be empty' };
  }

  // Check for conflicts with other actions
  for (const [id, existing] of Object.entries(currentShortcuts)) {
    if (id === actionId) continue; // Skip self
    
    if (shortcutsMatch(shortcut, existing)) {
      const action = SHORTCUT_ACTIONS.find((a) => a.id === id);
      return {
        valid: false,
        error: `Conflicts with "${action?.label || id}"`,
      };
    }
  }

  return { valid: true };
}

/**
 * Format shortcut for display
 */
export function formatShortcutDisplay(shortcut: CustomShortcut, isMac: boolean): string {
  const parts: string[] = [];

  if (shortcut.ctrlOrCmd) {
    parts.push(isMac ? '⌘' : 'Ctrl');
  }
  if (shortcut.shift) {
    parts.push(isMac ? '⇧' : 'Shift');
  }
  if (shortcut.alt) {
    parts.push(isMac ? '⌥' : 'Alt');
  }

  // Format the key
  const key = shortcut.key.length === 1 ? shortcut.key.toUpperCase() : shortcut.key;
  parts.push(key);

  return parts.join(isMac ? '' : '+');
}
